<script setup>
import { computed } from 'vue';
import Icon from 'next/icon/Icon.vue';
import { useInbox } from 'dashboard/composables/useInbox';
import { useMessageContext } from './provider.js';
import { MESSAGE_STATUS, MESSAGE_TYPES } from './constants';

// Per-message provider badge for SENT WhatsApp messages. Lives in its own component (not just
// inside MessageMeta) because message grouping hides the meta row on every message but the last
// of a group — and the provider can differ between grouped messages, so the badge must render
// per message regardless of grouping.
const { isAWhatsAppChannel, isAWhatsAppCloudChannel } = useInbox();
const { messageType, status, isPrivate, contentAttributes } =
  useMessageContext();

const PROVIDER_BADGES = {
  whatsapp_cloud: {
    label: 'Oficial',
    icon: 'i-lucide-shield-check',
    class: 'text-n-blue-11',
  },
  zapi: {
    label: 'api-alternativa',
    icon: 'i-lucide-zap',
    class: 'text-n-amber-11',
  },
};

const isSentWhatsApp = computed(() => {
  if (!isAWhatsAppChannel.value || isPrivate.value) return false;
  if (status.value === MESSAGE_STATUS.FAILED) return false;
  if (contentAttributes.value?.deleted) return false;
  return (
    messageType.value === MESSAGE_TYPES.OUTGOING ||
    messageType.value === MESSAGE_TYPES.TEMPLATE
  );
});

// sent_via is the authoritative server-set marker; transport is the composer hint for messages
// still in flight; the inbox's own provider is the fallback for legacy messages.
const badge = computed(() => {
  if (!isSentWhatsApp.value) return null;

  const attrs = contentAttributes.value || {};
  let signal = attrs.sentVia;
  if (!signal) {
    signal =
      attrs.transport === 'zapi'
        ? 'zapi'
        : (isAWhatsAppCloudChannel.value && 'whatsapp_cloud') || 'zapi';
  }
  return PROVIDER_BADGES[
    signal === 'whatsapp_cloud' ? 'whatsapp_cloud' : 'zapi'
  ];
});
</script>

<template>
  <span
    v-if="badge"
    class="inline-flex items-center gap-0.5 text-xs font-medium"
    :class="badge.class"
  >
    <Icon :icon="badge.icon" class="size-3" />
    {{ badge.label }}
  </span>
</template>
